import React from 'react';
import { DateField, FunctionField, ReferenceField, TextField } from 'react-admin';
import { getBusinessConf } from '../config';
import { formatSiretSiren, getCountryName } from '../../shared/country';

const sanitizeBusinessString = input => {
  if (input === 'string') {
    return '';
  }
  return input;
};

/**
 * Formats an address object into a readable one-line string, country included.
 * @param {Object} address An address object (address or billingAddress)
 * @returns {string} The formatted address
 */
const formatAddress = address => {
  if (!address) {
    return '';
  }

  const elements = [
    sanitizeBusinessString(address.streetName),
    sanitizeBusinessString(address.city),
    sanitizeBusinessString(address.postalCode),
    getCountryName(address.countryCode),
  ];

  return elements.filter(Boolean).join(', ');
};

/**
 * Country of the business, billing address first as on the business detail page.
 * @param {Object} business The business record
 * @returns {string} The country name, or '-' when neither address carries a country code
 */
const getBusinessCountry = business => {
  const countryCode =
    (business.billingAddress && business.billingAddress.countryCode) ||
    (business.address && business.address.countryCode);

  return getCountryName(countryCode) || '-';
};

export const BusinessFields = translate => [
  <TextField key="name" source="name" />,
  <TextField key="legalName" source="legalName" sortable={false} />,
  <ReferenceField
    reference="user"
    label={translate('resources.business.fields.businessOwner')}
    source="businessOwner.id"
    key="userFullName"
    sortable={false}
    linkType={false}
    allowEmpty
  >
    <TextField key="fullName" source="fullName" />
  </ReferenceField>,
  <DateField
    source="creationDate"
    key="creationDate"
    locales={
      getBusinessConf().config.translation &&
      getBusinessConf().config.translation.default &&
      getBusinessConf().config.translation.default.replace('_', '-')
    }
  />,
  <TextField key="status" source="status" />,
  <TextField key="id" source="id" />,
  <TextField key="fleetId" source="fleetId" />,
  <FunctionField
    key="billingAddress"
    label={translate('resources.business.fields.billingAddress.streetName')}
    render={business => formatAddress(business.billingAddress)}
  />,
  <FunctionField
    key="country"
    label={translate('resources.business.fields.country')}
    sortable={false}
    render={getBusinessCountry}
  />,
  <TextField key="phoneNumber" source="phoneNumber" sortable={false} />,
  <TextField key="faxNumber" source="faxNumber" sortable={false} />,
  <TextField key="email" source="email" sortable={false} />,
  <TextField key="vat" source="vat" sortable={false} />,
  <TextField key="vatVerified" source="vatVerified" sortable={false} />,
  <TextField key="ein" source="ein" sortable={false} />,
  <TextField key="purchaseOrderReference" source="purchaseOrderReference" sortable={false} />,
  <FunctionField
    key="siretSiren"
    label={translate('resources.business.fields.siretSiren')}
    sortable={false}
    render={record => formatSiretSiren(record.siretSiren)}
  />,
  <TextField key="electronicAddress" source="electronicAddress" sortable={false} />,
  <TextField key="registrationUrl" source="registrationUrl" sortable={false} />,
  <FunctionField
    key="address"
    label={translate('resources.business.fields.address.streetName')}
    sortable={false}
    render={business => formatAddress(business.address)}
  />,
  <TextField key="comment" source="comment" sortable={false} />,
  <TextField key="locale" source="locale" sortable={false} />,
  <TextField key="type" source="type" />,
  <TextField key="entityName" source="entityName" sortable={false} />,
  <TextField key="tokenbillingAddress" source="tokenbillingAddress" />,
  <TextField key="services" source="services" />,
  <TextField key="masterEntity" source="masterEntity" sortable={false} />,
  <TextField key="communityTaxNumber" source="communityTaxNumber" sortable={false} />,
  <TextField key="taxNumberCountry" source="taxNumberCountry" sortable={false} />,
];
